/**
 * Enhanced Protected Route Component
 * Supports both basic authentication and admin permission checks
 * 
 * Usage:
 * - Basic auth: <ProtectedRoute><Component /></ProtectedRoute>
 * - Admin auth: <ProtectedRoute requireAdmin><AdminComponent /></ProtectedRoute>
 * - Specific permission: <ProtectedRoute requireAdmin requiredPermission="admin:manage-users-and-resources"><Component /></ProtectedRoute>
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useAdminAuth } from '@/features/admin-portal/hooks/useAdminAuth';
import { Spinner } from '@/shared/components/Loading/Spinner';
import type { AdminPermission } from '@/features/admin-portal/types/admin-permissions.type';

type ProtectedRouteProps = {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requiredPermission?: AdminPermission;
};

export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requiredPermission 
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { isAdmin, permissions, isLoading: adminLoading } = useAdminAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  const loading = requireAdmin ? (isLoading || adminLoading) : isLoading;
  
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Spinner className="mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    console.log('ProtectedRoute: user not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirements if specified
  if (requireAdmin) {
    if (!isAdmin) {
      console.log('ProtectedRoute: user is not admin, redirecting to home');
      return <Navigate to="/" replace />;
    }

    if (requiredPermission) {
      const hasPermission = permissions.includes(requiredPermission);
      
      if (!hasPermission) {
        console.log(`ProtectedRoute: user missing permission ${requiredPermission}, redirecting to admin overview`);
        return <Navigate to="/admin" replace />;
      }
    }
  }

  // All checks passed - render protected content
  return <>{children}</>;
}
